import React, { useCallback, useEffect, useState } from 'react'
import DeleteIcon from '@mui/icons-material/Delete'
import { UiSchema } from '@rjsf/core'

import {
  Box,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Modal,
  FormControlLabel,
  Checkbox,
  IconButton,
} from '@mui/material'
import { JSONSchema7 } from 'json-schema'
// import { useAppDispatch } from '../../../redux/useTypeSelector'
// import { showAlert } from '../../../redux/ActionCreater'
import { getHeaders } from '../utils/common'
import AddNewNodeModal from './modals/AddNewNodeModal'

export type KindOption = {
  name: string
  categories: string[]
  is_deprecated: boolean
  default:boolean
}

export type ConfigInfo = {
  schema: JSONSchema7
  uiSchema: UiSchema
}

export interface FieldConfig {
  name: string
  type: string
  description: string
  default: any
  required: boolean
}

const EditNodes: React.FC = () => {
  // const dispatch = useAppDispatch();
  const [sessions, setSessions] = useState<any[]>([])
  const [kindOptions, setKindOptions] = useState<KindOption[] | null>(null)
  const [isBenthosEnabled, setIsBenthosEnabled] = useState<boolean>(false)
  const [typeSearch, setTypeSearch] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentOption, setCurrentOption] = useState<KindOption | null>(null)
  const [selectedOption, setSelectedOption] = useState<string>('input')
  const [configInfo, setConfigInfo] = useState<ConfigInfo | null>(null)

  const [fieldName, setFieldName] = useState('')
  const [fieldType, setFieldType] = useState('string')
  const [fieldDescription, setFieldDescription] = useState('')
  const [fieldDefault, setFieldDefault] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [newFields, setNewFields] = useState<FieldConfig[]>([])
  const [fieldRequired, setFieldRequired] = useState(false)

  const [isNewNodeAdd, setIsNewNodeAdd] = useState<boolean>(false)

  const supportedTypes = [ 'boolean', 'string', 'number']
  const optionAction = [
    { value: 'input', label: 'Inputs' },
    { value: 'output', label: 'Outputs' },
    { value: 'processor', label: 'Processors' },
  ]


  const validateField = () => {
    if (!fieldName.trim()) {
      setErrorMessage('Field name is required.')
      return false
    }
    if (!supportedTypes.includes(fieldType)) {
      setErrorMessage(`Unsupported field type '${fieldType}'. Supported types are: ${supportedTypes.join(', ')}.`)
      return false
    }
    setErrorMessage('')
    return true
  }

  const handleAddField = () => {
    if (!validateField()) {
      // dispatch(showAlert('error', 'Validation failed. Please check the field details.', true))
      return
    }
  
    const parsedDefault =
      fieldType === 'boolean'
        ? fieldDefault.toLowerCase() === 'true'
        : fieldType === 'number'
        ? Number(fieldDefault)
        : fieldType === 'string'
        ? String(fieldDefault)
        : fieldDefault
  
    const newField: FieldConfig = {
      name: fieldName,
      type: fieldType,
      description: fieldDescription,
      default: parsedDefault,
      required: fieldRequired,
    }
  
    setNewFields((prevFields: FieldConfig[]) => [...prevFields, newField])
    setFieldName('')
    setFieldType('string')
    setFieldDescription('')
    setFieldDefault('')
    setFieldRequired(false)
    // dispatch(showAlert('success', 'Field added successfully.', true))
  }

  const getSessions = useCallback(() => {
    let isSubscribed = true

    fetch('/mqttapi/v1/sessions', {
      headers: getHeaders(),
      method: 'GET',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch sessions: ${response.statusText}`)
        }
        return response.json()
      })
      .then((json) => {
        if (isSubscribed) {
          if (!json?.benthosNotAdded) {
            setSessions(json)
            setIsBenthosEnabled(true)
          } else {
            setIsBenthosEnabled(false)
          }
        }
      })
      .catch((error) => {
        console.error('Error fetching sessions:', error)
      })

    return () => {
      isSubscribed = false
    }
  }, [])

  useEffect(() => {
    getSessions()
  }, [getSessions])

  useEffect(() => {
    if (sessions.length > 0) {
      const sessionId = sessions[0]?.id
      if (sessionId && selectedOption) {
        fetch(`/mqttapi/v1/session/${sessionId}/list/${selectedOption}`, {
          headers: getHeaders(),
          method: 'GET',
        })
          .then((response) => {
            if (!response.ok) {
              return response.text().then((text) => {
                throw new Error(`Error: ${response.statusText} - ${text}`)
              })
            }
            return response.json()
          })
          .then((data) => {
            setKindOptions(data)
          })
          .catch((error) => {
            console.error(`Error fetching session ${selectedOption} list:`, error)
          })
      }
    }
  }, [sessions, selectedOption])

  const handleEdit = (option: KindOption) => {
    setCurrentOption(option)
    setIsModalOpen(true)

    const sessionId = sessions[0]?.id

    if (!sessionId) {
      return
    }

    let subscribed = true

    fetch(`/mqttapi/v1/session/${sessionId}/config/${selectedOption}/${option?.name}`, {
      headers: getHeaders(),
      method: 'GET',
    })
      .then(async (response) => {
        if (response.ok) {
          return response.json()
        }
        const errorText = await response.text()
        throw new Error(`${response.status}: ${response.statusText}: ${errorText}`)
      })
      .then((json) => {
        if (!subscribed) return

        // Normalize schema properties
        const normalizedSchema: JSONSchema7 = {
          type: json.schema?.type || 'object',
          properties: json.schema?.properties || {},
          required: json.schema?.required || [],
          additionalProperties: typeof json.schema?.additionalProperties === 'boolean' ? json.schema.additionalProperties : false,
        }

        // let normalizedUiSchema
        // // Map UI schema if present
        // if (json.uiSchema) {
        //   normalizedUiSchema = {
        //     ...json.uiSchema,
        //   }
        // } else if (json.uischema) {
        //   normalizedUiSchema = {
        //     ...json.uischema,
        //   }
        // }
        const normalizedUiSchema = {
          ...json.uiSchema,
        }

        // Prepare ConfigInfo object
        const tmpInfo: ConfigInfo = {
          schema: normalizedSchema,
          uiSchema: normalizedUiSchema,
        }

        setConfigInfo(tmpInfo)
      })
      .catch((error) => {
        console.error('Error fetching config:', error)
      })

    // Cleanup subscription
    return () => {
      subscribed = false
    }
  }

  const handleSaveFields = async () => {
    if (configInfo) {
      const updatedSchema = { ...configInfo.schema }
      const updatedUiSchema = { ...configInfo.uiSchema }
  
      newFields.forEach((field: any) => {
        if (!updatedSchema.properties) updatedSchema.properties = {}
        if (!updatedSchema.required) updatedSchema.required = []
        if (!updatedUiSchema['ui:order']) updatedUiSchema['ui:order'] = []
  
        // Update schema properties
        updatedSchema.properties[field.name] = {
          type: field.type,
          description: field.description,
          default: field.default,
        }
  
        // Mark field as required if specified
        if (field.required) {
          updatedSchema.required.push(field.name)
        }
  
        // Update UI schema order
        updatedUiSchema['ui:order'].push(field.name)
      })
  
      configInfo.schema = updatedSchema
      configInfo.uiSchema = updatedUiSchema
  
      try {
        const sessionId = sessions[0]?.id
        const response = await fetch(`/mqttapi/v1/session/${sessionId}/config/${selectedOption}/${currentOption?.name}`, {
          headers: {
            ...getHeaders(),
            'Content-Type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify(configInfo),
        })
  
        if (!response.ok) {
          throw new Error(`Failed to update config: ${response.statusText}`)
        }
        // dispatch(showAlert('success', 'Fields saved successfully.', true))
        
      } catch (error) {
        console.error('Error updating config:', error)
        // dispatch(showAlert('error', 'Failed to save fields. Please try again.', true))
}
  
      setNewFields([])
      setIsModalOpen(false)
    }
  }

  const handleRemoveField = (fieldName: string, isFromConfig: boolean) => {
    if (isFromConfig && configInfo?.schema) {
      const updatedSchema = { ...configInfo.schema }
      const updatedUiSchema = { ...configInfo.uiSchema }

      // Ensure properties exist
      updatedSchema.properties = updatedSchema.properties || {}

      // Remove the field from schema properties
      if (updatedSchema.properties[fieldName]) {
        delete updatedSchema.properties[fieldName]
      }

      // Update the 'required' array safely
      if (updatedSchema.required) {
        updatedSchema.required = updatedSchema.required.filter((reqField) => reqField !== fieldName)
      }

      // Remove the field from UI schema order
      if (updatedUiSchema['ui:order']) {
        updatedUiSchema['ui:order'] = updatedUiSchema['ui:order'].filter((orderField:any) => orderField !== fieldName)
      }

      // Update config info
      setConfigInfo({
        ...configInfo,
        schema: updatedSchema,
        uiSchema: updatedUiSchema,
      })
    } else {
      // Remove the field from newFields for local changes
      setNewFields((prevFields) => prevFields.filter((field) => field.name !== fieldName))
    }
  }

  const handleDeleteNode = async (name: string) => {
    try {
      const sessionId = sessions[0]?.id
  
      if (!sessionId) {
        console.error('Session ID is not available.')
        return
      }
  
      const response = await fetch(`/mqttapi/v1/session/${sessionId}/list/${selectedOption}/${name}`, {
        headers: {
          ...getHeaders(),
          'Content-Type': 'application/json',
        },
        method: 'DELETE',
      })
  
      if (!response.ok) {
        throw new Error('Failed to delete node')
      }
  
      const updatedKindOptions = kindOptions?.filter((opt) => opt.name !== name)
      setKindOptions(updatedKindOptions || [])
  
      // dispatch(showAlert('success','Node deleted successfully.', true ))
      
    } catch (error) {
      console.error('Error deleting node:', error)
      // dispatch(showAlert('error','Failed to delete node. Please try again.', true ))
    }
  }

  const handleNewAddNode = () => {
    setIsNewNodeAdd(true)
  }
  
  return (
    <Box sx={{ fontFamily: 'Arial, sans-serif', p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
        }}
      >
        <Typography variant="h4" sx={{ margin: 0 }}>
          Edit Nodes
        </Typography>
        <Box display={'flex'} gap={'1rem'}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="actionSelect-label">Select Option</InputLabel>
            <Select
              labelId="actionSelect-label"
              id="actionSelect"
              value={selectedOption}
              onChange={(e) => setSelectedOption(e.target.value)}
              label="Select Option"
            >
              {optionAction.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <Button variant="contained" color="secondary" onClick={handleNewAddNode}>Add new node</Button>
          </FormControl>
        </Box>
      </Box>

      <TextField
        fullWidth
        placeholder="🔍 Search..."
        value={typeSearch}
        onChange={(event) => setTypeSearch(event.target.value)}
        sx={{ mb: 2 }}
      />

      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Categories</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(kindOptions || [])
              .filter((opt) => {
                if (opt.is_deprecated) return false
                if (typeSearch === '') return true
                const searchTerms = typeSearch.split(' ')
                const categories = opt.categories || []
                const content = `${opt.name.toLowerCase()} ${categories.join(' ').toLowerCase()}`
                return searchTerms.every((term) => content.includes(term.toLowerCase()))
              })
              .map((opt, idx) => (
                <TableRow key={idx}>
                  <TableCell>{opt.name}</TableCell>
                  <TableCell>{(opt.categories || []).join(', ')}</TableCell>
                  <TableCell>
                    <Button variant="outlined" onClick={() => handleEdit(opt)} size="small" sx={{ borderColor: 'black', color: 'black' }}>
                      Edit
                    </Button>
                    {
                      !opt.default &&  <IconButton
                      style={{marginLeft:"1rem"}}
                      onClick={()=>handleDeleteNode(opt.name)}
                      >
                        <DeleteIcon color='error' />
                      </IconButton>
                    }
                   
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'white',
            p: 4,
            borderRadius: 2,
            width: { xs: '95%', sm: '80%' },
            maxHeight: '85vh',
            overflowY: 'auto',
            boxShadow: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              gap: 2,
            }}
          >
            <Box
              sx={{
                flex: 1,
                borderRight: { sm: '1px solid black' },
                pr: { sm: 2 },
                maxHeight: { xs: '50vh', sm: '70vh' },
                overflowY: 'auto',
              }}
            >
              <Typography variant="h6">List of Fields</Typography>
              <TableContainer component={Paper} style={{ marginTop: '20px' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Name</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Type</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Default Value</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Action</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Render existing schema fields */}
                    {configInfo?.schema?.properties && Object.entries(configInfo.schema.properties).length > 0
                      ? Object.entries(configInfo.schema.properties).map(([fieldName, fieldConfig]: [string, any]) => (
                          <TableRow key={fieldName}>
                            <TableCell>{fieldName}</TableCell>
                            <TableCell>{fieldConfig.type || 'N/A'}</TableCell>
                            <TableCell>{fieldConfig.default?.toString() || 'None'}</TableCell>
                            <TableCell>
                              <Button variant="outlined" color="error" size="small" onClick={() => handleRemoveField(fieldName, true)}>
                                Remove
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      : null}

                    {/* Render newly added fields */}
                    {newFields.length > 0
                      ? newFields.map((field) => (
                          <TableRow key={field.name}>
                            <TableCell>{field.name}</TableCell>
                            <TableCell>{field.type}</TableCell>
                            <TableCell>{field.default?.toString() || 'None'}</TableCell>
                            <TableCell>
                              <Button variant="outlined" color="error" size="small" onClick={() => handleRemoveField(field.name, false)}>
                                Remove
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      : null}

                    {/* Fallback message if no fields exist */}
                    {Object.entries(configInfo?.schema?.properties || {}).length === 0 && newFields.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No fields available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Add New Fields
              </Typography>
              <TextField label="Field Name" sx={{ mb: 2 }} value={fieldName} onChange={(e) => setFieldName(e.target.value)} fullWidth />
              <TextField
                label="Field Type"
                value={fieldType}
                onChange={(e) => setFieldType(e.target.value)}
                select
                fullWidth
                helperText={`Supported types: ${supportedTypes.join(', ')}`}
                sx={{ mb: 2 }}
              >
                {supportedTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Field Description"
                value={fieldDescription}
                onChange={(e) => setFieldDescription(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              />
              <TextField label="Field Default Value" value={fieldDefault} onChange={(e) => setFieldDefault(e.target.value)} fullWidth />
              <FormControlLabel
                control={<Checkbox checked={fieldRequired} onChange={(e) => setFieldRequired(e.target.checked)} />}
                label="Required"
              />
              {errorMessage && (
                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                  {errorMessage}
                </Typography>
              )}
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" color="primary" onClick={handleAddField}>
                  Add Field
                </Button>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="contained" color="secondary" onClick={handleSaveFields}>
              Save
            </Button>
            <Button variant="contained" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
      <AddNewNodeModal setKindOptions={setKindOptions} isModalOpen={isNewNodeAdd} setIsModalOpen={setIsNewNodeAdd} sessions={sessions} selectedOption={selectedOption} />
    </Box>
  )
}

export default EditNodes
